import { useMemo } from 'react';
import { UserButton } from '@clerk/clerk-react';
import {
  Home,
  ListChecks,
  PlusCircle,
  BarChart3,
  Wallet,
  Search,
  Inbox,
  Sparkles,
  Lightbulb,
  FileSpreadsheet,
  Contact,
} from 'lucide-react';
import { BrandMark } from './BrandMark';
import { ThemeToggle } from './ThemeToggle';
import { Shell, type NavSection } from './Shell';
import { ViewSwitcher } from './ViewSwitcher';
import { TooltipProvider } from '@/components/ui/tooltip';
import { useAssignedCounts } from '@/hooks/useDiscoveryQueue';
import { useMe } from '@/hooks/useMe';

export function AppLayout(): JSX.Element {
  const assignedCounts = useAssignedCounts();
  const badge = (assignedCounts.data?.direct ?? 0) + (assignedCounts.data?.team ?? 0);
  const me = useMe();
  // Only for outreachers an admin has allowed (team rule or personal override).
  const bulkImport = me.data?.role === 'outreacher' && me.data.bulkImportEnabled === true;

  const sections: NavSection[] = useMemo(
    () => [
      {
        label: null,
        items: [
          { to: '/portal', label: 'Dashboard', icon: Home, end: true },
          // The flow: Found → Messaged → Replied (the log) → Joined → Posted.
          { to: '/portal/log', label: 'Log reply', icon: PlusCircle },
          { to: '/portal/invites', label: 'My creators', icon: ListChecks },
          ...(bulkImport ? [{ to: '/portal/bulk-import', label: 'Bulk import', icon: FileSpreadsheet }] : []),
          { to: '/portal/discovery-queue', label: 'Lead queue', icon: Sparkles, badge },
          { to: '/portal/prospects', label: 'Creator lookup', icon: Search },
          { to: '/portal/search-ideas', label: 'Search ideas', icon: Lightbulb },
          { to: '/portal/creator-requests', label: 'Creator requests', icon: Inbox },
          { to: '/portal/rates', label: 'Rates', icon: BarChart3 },
          { to: '/portal/payouts', label: 'Payouts', icon: Wallet },
          { to: '/portal/profile', label: 'My details', icon: Contact },
        ],
      },
    ],
    [badge, bulkImport],
  );

  return (
    <TooltipProvider delayDuration={200} skipDelayDuration={300}>
      <Shell
        brand={<BrandMark />}
        sections={sections}
        scopeLabel="Outreacher"
        headerSlot={
          <>
            <ViewSwitcher />
            <ThemeToggle />
            <UserButton afterSignOutUrl="/sign-in" />
          </>
        }
      />
    </TooltipProvider>
  );
}
